import { Pencil, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@web/components/ui/dialog";
import { Badge } from "@web/components/ui/badge";
import { ColorBadge } from "@web/components/ui/color-badge";
import { Button } from "@web/components/ui/button";
import { cn, formatCurrency, formatDateMedium } from "@repo/share-log-web-utils";
import type { LogWithRelations } from "@repo/share-log-interfaces";
import { LogType } from "@repo/share-log-interfaces";

interface LogDetailDialogProps {
  log: LogWithRelations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isOwner: boolean;
  onEdit: (log: LogWithRelations) => void;
  onDelete: (logId: number) => void;
}

export function LogDetailDialog({
  log,
  open,
  onOpenChange,
  isOwner,
  onEdit,
  onDelete,
}: LogDetailDialogProps) {
  if (!log) return null;

  const isExpense = log.type === LogType.EXPENSE;

  const handleEdit = () => {
    onOpenChange(false);
    onEdit(log);
  };

  const handleDelete = () => {
    onDelete(log.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>기록 상세</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Amount & Type */}
          <div className="flex items-center justify-between">
            <Badge variant={isExpense ? "expense" : "income"}>
              {isExpense ? "지출" : "수입"}
            </Badge>
            <span
              className={cn(
                "text-2xl font-bold",
                isExpense ? "text-expense" : "text-income",
              )}
            >
              {isExpense ? "-" : "+"}
              {formatCurrency(log.amount)}
            </span>
          </div>

          {/* Details */}
          <div className="space-y-3">
            <DetailRow label="날짜" value={formatDateMedium(log.date)} />

            {log.description && (
              <DetailRow label="설명" value={log.description} />
            )}

            {log.category && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">카테고리</span>
                <ColorBadge
                  name={log.category.name}
                  color={log.category.color}
                />
              </div>
            )}

            {log.method && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">결제수단</span>
                <ColorBadge
                  name={log.method.name}
                  color={log.method.color}
                  icon={log.method.icon}
                />
              </div>
            )}

            {log.memo && (
              <div className="space-y-1">
                <span className="text-sm text-muted-foreground">메모</span>
                <p className="text-sm bg-muted rounded-md px-3 py-2">
                  {log.memo}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">작성자</span>
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full overflow-hidden border shrink-0">
                  {log.user.avatarUrl ? (
                    <img
                      src={log.user.avatarUrl}
                      alt={log.user.nickname || "사용자"}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                      {(log.user.nickname?.[0] || "?").toUpperCase()}
                    </div>
                  )}
                </div>
                <span className="text-sm">
                  {log.user.nickname || "알 수 없음"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {isOwner && (
          <DialogFooter>
            <Button variant="outline" onClick={handleEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              수정
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              삭제
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
